import type { FingerprintResult } from './fingerprint';
import { snapshotSignature, type CollectSnapshot } from './snapshot';
import { shouldRefreshFingerprintForViewport } from './viewport-bands';

export type CollectReason =
  | 'boot'
  | 'url_change'
  | 'viewport_change'
  | 'visibility_resume'
  | 'fingerprint_refresh';

type TransportLike = {
  post: (
    path: string,
    body: unknown,
  ) => Promise<{ ok: boolean; status?: number; body: unknown | null }>;
};

export function createCollectCoordinator(opts: {
  deviceUuid: string;
  getCollectionId: () => string | null;
  setCollectionId: (cid: string) => void;
  transport: TransportLike;
  readSnapshot: () => CollectSnapshot;
  collectFingerprint: () => Promise<FingerprintResult | null>;
  sdkVersion: string;
}) {
  const pendingReasons = new Set<CollectReason>();
  let lastSignature: string | null = null;
  let lastFingerprint: FingerprintResult | null = null;
  let lastViewport: CollectSnapshot['viewport'] | null = null;
  let flushTimer: ReturnType<typeof setTimeout> | null = null;
  let viewportTimer: ReturnType<typeof setTimeout> | null = null;
  let forceFingerprint = false;

  function schedule(delayMs: number, force = false): void {
    forceFingerprint = forceFingerprint || force;
    if (flushTimer !== null) clearTimeout(flushTimer);
    flushTimer = setTimeout(() => {
      flushTimer = null;
      if (viewportTimer !== null) return;
      void flush();
    }, delayMs);
  }

  async function flush(): Promise<void> {
    const snapshot = opts.readSnapshot();
    const previousHash = lastFingerprint?.hash ?? null;
    let fingerprint = lastFingerprint;
    let includeComponents = false;

    if (forceFingerprint || shouldRefreshFingerprintForViewport(lastViewport, snapshot.viewport)) {
      const refreshed = await opts.collectFingerprint();
      if (refreshed) {
        fingerprint = refreshed;
        includeComponents = refreshed.hash !== previousHash || previousHash === null;
        if (includeComponents) pendingReasons.add('fingerprint_refresh');
      }
    }

    const signature = snapshotSignature(snapshot, fingerprint?.hash ?? previousHash);
    const shouldSendWithoutChange =
      forceFingerprint || pendingReasons.has('visibility_resume') || pendingReasons.has('fingerprint_refresh');
    if (signature === lastSignature && !shouldSendWithoutChange) {
      pendingReasons.clear();
      forceFingerprint = false;
      return;
    }

    const payload = {
      collectionId: opts.getCollectionId() ?? undefined,
      deviceUuid: opts.deviceUuid,
      sdkVersion: opts.sdkVersion,
      reasons: Array.from(pendingReasons),
      snapshot,
      fingerprint: fingerprint
        ? {
            visitorId: fingerprint.visitorId,
            hash: fingerprint.hash,
            components: includeComponents ? fingerprint.components : null,
          }
        : null,
    };

    const result = await opts.transport.post('/v1/collect', payload);
    if (result.ok && typeof result.body === 'object' && result.body !== null) {
      const body = result.body as { collectionId?: unknown };
      if (typeof body.collectionId === 'string') {
        opts.setCollectionId(body.collectionId);
      }
      lastSignature = signature;
      lastFingerprint = fingerprint;
      lastViewport = snapshot.viewport;
    }

    pendingReasons.clear();
    forceFingerprint = false;
  }

  return {
    trigger(reason: Exclude<CollectReason, 'visibility_resume' | 'fingerprint_refresh'>): void {
      pendingReasons.add(reason);
      schedule(0, reason === 'boot');
    },
    triggerViewportChange(): void {
      pendingReasons.add('viewport_change');
      if (viewportTimer !== null) clearTimeout(viewportTimer);
      viewportTimer = setTimeout(() => {
        viewportTimer = null;
        schedule(0, false);
      }, 400);
    },
    triggerVisibilityResume(): void {
      pendingReasons.add('visibility_resume');
      schedule(0, true);
    },
  };
}
